/**
 * The shared wire validators, as the application uses them.
 *
 * The validating functions here are **precompiled**: Ajv generated them ahead of
 * time with its standalone code generator
 * (`scripts/generate-validators.mjs` → `src/generated/validators.js`) and this
 * module imports them, rather than building an Ajv instance and calling
 * `ajv.compile(schema)` when a value first arrives. That difference is why the
 * browser bundle needs no `'unsafe-eval'` in the content security policy: Ajv
 * compiles a schema into a function with `new Function`, and nothing on this path
 * compiles a schema any more. The options the generated code was built from live
 * in `strict-ajv.ts`, and the tests that hold the generated module to them — and
 * to the schemas — are in `validator.test.ts`.
 *
 * The API's *route* schemas are a separate matter: Fastify compiles those with its
 * own Ajv, configured from the same `strictAjvOptions`, and they never reach a
 * browser.
 */
import { validateErrorEnvelope, validateHealthResponse, validateLedgerAccountEnvelope, validateLedgerAccountsEnvelope, validateLedgerBulkPreviewEnvelope, validateLedgerBulkResultEnvelope, validateLedgerCategoriesEnvelope, validateLedgerCategoryEnvelope, validateLedgerEntriesEnvelope, validateLedgerEntryEnvelope, validateLedgerEntryLifecyclePreviewEnvelope, validateLedgerEntrySuggestionsEnvelope, validateLedgerForecastEnvelope, validateLedgerInstallmentGroupEnvelope, validateLedgerMutationPreviewEnvelope, validateLedgerRecurrenceEnvelope, validateLedgerRecurrencePreviewEnvelope, validateLedgerRecurrenceTemplateEnvelope, validateLedgerRecurrencesEnvelope, validateLedgerReportsEnvelope, validateLedgerTransferEnvelope, validateLedgerTransferPairSuggestionsEnvelope, validateLedgerTransferPreviewEnvelope, validateLedgerTransfersEnvelope, validateSessionState, } from './generated/validators.js';
export class InvalidContractError extends Error {
    name = 'InvalidContractError';
    errors;
    constructor(what, errors) {
        super(`invalid ${what}`);
        this.errors = errors;
    }
}
/** Validate an unknown value as a `/health` response body, or throw. */
export function parseHealthResponse(value) {
    if (!validateHealthResponse(value)) {
        throw new InvalidContractError('health response', validateHealthResponse.errors);
    }
    return value;
}
/** Validate an unknown value as a session-state body, or throw. */
export function parseSessionState(value) {
    if (!validateSessionState(value)) {
        throw new InvalidContractError('session state', validateSessionState.errors);
    }
    return value;
}
/** Validate an unknown value as an error envelope body, or throw. */
export function parseErrorEnvelope(value) {
    if (!validateErrorEnvelope(value)) {
        throw new InvalidContractError('error envelope', validateErrorEnvelope.errors);
    }
    return value;
}
/**
 * The ledger reads the monthly UI performs (P4-T2).
 *
 * They are here rather than in the client so the browser and the API's own response
 * schemas stay one description: a wire change fails in the client with a named contract
 * error instead of being rendered. Each is the precompiled validator for its named
 * `$id`, so validating a response compiles no schema and needs no `'unsafe-eval'`.
 */
export function parseLedgerAccountEnvelope(value) {
    if (!validateLedgerAccountEnvelope(value)) {
        throw new InvalidContractError('ledger account envelope', validateLedgerAccountEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the accounts collection envelope, or throw. */
export function parseLedgerAccountsEnvelope(value) {
    if (!validateLedgerAccountsEnvelope(value)) {
        throw new InvalidContractError('ledger accounts envelope', validateLedgerAccountsEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the single-entry resource envelope, or throw. */
export function parseLedgerEntryEnvelope(value) {
    if (!validateLedgerEntryEnvelope(value)) {
        throw new InvalidContractError('ledger entry envelope', validateLedgerEntryEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the entries collection envelope, or throw. */
export function parseLedgerEntriesEnvelope(value) {
    if (!validateLedgerEntriesEnvelope(value)) {
        throw new InvalidContractError('ledger entries envelope', validateLedgerEntriesEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the installment-group resource envelope, or throw. */
export function parseLedgerInstallmentGroupEnvelope(value) {
    if (!validateLedgerInstallmentGroupEnvelope(value)) {
        throw new InvalidContractError('ledger installment group envelope', validateLedgerInstallmentGroupEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the mutation-preview resource envelope, or throw. */
export function parseLedgerMutationPreviewEnvelope(value) {
    if (!validateLedgerMutationPreviewEnvelope(value)) {
        throw new InvalidContractError('ledger mutation preview envelope', validateLedgerMutationPreviewEnvelope.errors);
    }
    return value;
}
/**
 * The transfer reads and the stored payment preview (P6-T2).
 *
 * `parseLedgerMutationPreviewEnvelope` above validates the *installment* preview shape;
 * a `transfer.*` preview is a different object and is validated here, so a payment
 * preview can never be rendered as if it were an installment one (they share the
 * envelope and nothing else).
 */
export function parseLedgerTransferEnvelope(value) {
    if (!validateLedgerTransferEnvelope(value)) {
        throw new InvalidContractError('ledger transfer envelope', validateLedgerTransferEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the transfers collection envelope, or throw. */
export function parseLedgerTransfersEnvelope(value) {
    if (!validateLedgerTransfersEnvelope(value)) {
        throw new InvalidContractError('ledger transfers envelope', validateLedgerTransfersEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the transfer-preview resource envelope, or throw. */
export function parseLedgerTransferPreviewEnvelope(value) {
    if (!validateLedgerTransferPreviewEnvelope(value)) {
        throw new InvalidContractError('ledger transfer preview envelope', validateLedgerTransferPreviewEnvelope.errors);
    }
    return value;
}
/**
 * The previously used account pairs (P7-T3). Validated like every other read, so the reuse list the
 * surface offers is one the contract accepted rather than one the client assembled.
 */
export function parseLedgerTransferPairSuggestionsEnvelope(value) {
    if (!validateLedgerTransferPairSuggestionsEnvelope(value)) {
        throw new InvalidContractError('ledger transfer pair suggestions envelope', validateLedgerTransferPairSuggestionsEnvelope.errors);
    }
    return value;
}
/**
 * The category and lifecycle reads (P7-T1).
 *
 * A category read, the owner's categories and an autocomplete suggestion list each get their own
 * precompiled validator for the same reason the earlier reads do: the browser renders only what
 * it has validated, and it does so without compiling a schema.
 */
export function parseLedgerCategoryEnvelope(value) {
    if (!validateLedgerCategoryEnvelope(value)) {
        throw new InvalidContractError('ledger category envelope', validateLedgerCategoryEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the categories collection envelope, or throw. */
export function parseLedgerCategoriesEnvelope(value) {
    if (!validateLedgerCategoriesEnvelope(value)) {
        throw new InvalidContractError('ledger categories envelope', validateLedgerCategoriesEnvelope.errors);
    }
    return value;
}
/**
 * A stored `entry.delete` / `entry.duplicate` preview. It shares the envelope with the installment
 * and transfer previews and nothing else, so it is validated separately rather than rendered as
 * one of them.
 */
export function parseLedgerEntryLifecyclePreviewEnvelope(value) {
    if (!validateLedgerEntryLifecyclePreviewEnvelope(value)) {
        throw new InvalidContractError('ledger entry lifecycle preview envelope', validateLedgerEntryLifecyclePreviewEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the autocomplete suggestions collection envelope, or throw. */
export function parseLedgerEntrySuggestionsEnvelope(value) {
    if (!validateLedgerEntrySuggestionsEnvelope(value)) {
        throw new InvalidContractError('ledger entry suggestions envelope', validateLedgerEntrySuggestionsEnvelope.errors);
    }
    return value;
}
/**
 * The P7-T2 reads: the per-currency report and one account's forecast.
 *
 * Both are validated before anything renders them, for the reason every read above is: the browser
 * shows what it has accepted, and it accepts it without compiling a schema. The forecast's coverage
 * is part of the shape rather than a separate field a client could ignore — a body that claims a
 * complete projection is refused here, which is where `RPT-04` ("never fabricate complete future
 * totals") becomes enforceable in the client instead of merely intended.
 */
export function parseLedgerReportsEnvelope(value) {
    if (!validateLedgerReportsEnvelope(value)) {
        throw new InvalidContractError('ledger reports envelope', validateLedgerReportsEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the forecast resource envelope, or throw. */
export function parseLedgerForecastEnvelope(value) {
    if (!validateLedgerForecastEnvelope(value)) {
        throw new InvalidContractError('ledger forecast envelope', validateLedgerForecastEnvelope.errors);
    }
    return value;
}
/**
 * The P8-T2 reads and the stored recurrence previews.
 *
 * Three shapes, three validators — the collection of schedules, one schedule with its classified
 * occurrences, and a stored create/stop preview — because they share the envelope and nothing
 * else. A stop preview accepted here is a body whose cancellation set, edited exceptions and
 * exclusions are all present: the surface cannot render a preview that left the set out, which is
 * the client-side half of "the preview lists them and exclusions".
 */
export function parseLedgerRecurrencesEnvelope(value) {
    if (!validateLedgerRecurrencesEnvelope(value)) {
        throw new InvalidContractError('ledger recurrences envelope', validateLedgerRecurrencesEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as the single-schedule resource envelope, or throw. */
export function parseLedgerRecurrenceEnvelope(value) {
    if (!validateLedgerRecurrenceEnvelope(value)) {
        throw new InvalidContractError('ledger recurrence envelope', validateLedgerRecurrenceEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as a stored recurrence preview, or throw. */
export function parseLedgerRecurrencePreviewEnvelope(value) {
    if (!validateLedgerRecurrencePreviewEnvelope(value)) {
        throw new InvalidContractError('ledger recurrence preview envelope', validateLedgerRecurrencePreviewEnvelope.errors);
    }
    return value;
}
/**
 * Validate an unknown value as the commit answer of a recurrence action, or throw.
 *
 * A creation and a stop answer with the same body a schedule read answers with, so a surface can
 * reuse the one renderer — and it is validated rather than trusted, like every other read.
 */
export function parseLedgerRecurrenceTemplateEnvelope(value) {
    if (!validateLedgerRecurrenceTemplateEnvelope(value)) {
        throw new InvalidContractError('ledger recurrence template envelope', validateLedgerRecurrenceTemplateEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as a stored bulk preview, or throw. */
export function parseLedgerBulkPreviewEnvelope(value) {
    if (!validateLedgerBulkPreviewEnvelope(value)) {
        throw new InvalidContractError('ledger bulk preview envelope', validateLedgerBulkPreviewEnvelope.errors);
    }
    return value;
}
/** Validate an unknown value as a bulk commit's answer, or throw. */
export function parseLedgerBulkResultEnvelope(value) {
    if (!validateLedgerBulkResultEnvelope(value)) {
        throw new InvalidContractError('ledger bulk result envelope', validateLedgerBulkResultEnvelope.errors);
    }
    return value;
}
//# sourceMappingURL=validator.js.map